import {
  buildingHandoverCatalogue,
  handoverApplicability,
  handoverSupplyStatuses,
  blankBuildingHandoverRow,
  type BuildingHandoverRow,
} from "@myfm/domain";
const text = (v: unknown) => (v === null || v === undefined ? "" : String(v).trim());
function date(v: unknown) {
  if (v === null || v === undefined || v === "") return "";
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  const s = text(v);
  if (
    /^\d{4}-\d{2}-\d{2}$/.test(s) &&
    !Number.isNaN(Date.parse(s)) &&
    new Date(s).toISOString().slice(0, 10) === s
  )
    return s;
  throw Error("Use a valid Excel date in the record date and due date columns.");
}
/** Imports transfer annotations only. Never accept an external review or alter
 * authoritative finance, contact, document or safety records from a workbook. */
export function parseHandoverWorkbook(sheets: Record<string, unknown[][]>) {
  const result: Record<string, BuildingHandoverRow> = {};
  const seen = new Set<string>();
  for (const [name, pack] of [
    ["Standard checklist", "Standard"],
    ["Additional HRB", "HRB"],
  ] as const) {
    const rows = sheets[name];
    if (!rows) throw Error(`The workbook is missing the ${name} sheet.`);
    if (
      rows[5]?.[0] !== "Item ID" ||
      rows[5]?.[5] !== "Applies?" ||
      rows[5]?.[6] !== "Supply status"
    )
      throw Error(`The ${name} columns do not match the handover template.`);
    for (const cells of rows.slice(6)) {
      const id = text(cells[0]);
      if (!id) continue;
      if (!buildingHandoverCatalogue.some((item) => item.id === id && item.pack === pack))
        throw Error(`Unknown checklist reference: ${id}`);
      if (seen.has(id)) throw Error(`Duplicate checklist reference: ${id}`);
      seen.add(id);
      const applies = text(cells[5]) || "To confirm",
        status = text(cells[6]) || "Not supplied";
      if (
        !handoverApplicability.includes(applies as never) ||
        !handoverSupplyStatuses.includes(status as never)
      )
        throw Error(`Choose a valid applicability and supply status for ${id}.`);
      const row = {
        ...blankBuildingHandoverRow(),
        applies: applies as BuildingHandoverRow["applies"],
        status: status as BuildingHandoverRow["status"],
        reference: text(cells[7]),
        recordDate: date(cells[8]),
        holder: text(cells[9]),
        dueDate: date(cells[10]),
        nextAction: text(cells[11]),
        notes: text(cells[13]),
      };
      if (applies === "No" && !row.notes) throw Error(`Explain why ${id} does not apply.`);
      if (Object.values(row).some((value) => value.length > 5000) || row.holder.length > 240)
        throw Error(`The entry for ${id} is too long.`);
      // Untouched template rows must not erase existing entries.
      if (JSON.stringify(row) !== JSON.stringify(blankBuildingHandoverRow())) result[id] = row;
    }
  }
  const outstanding = sheets["Outstanding information"];
  if (outstanding) {
    if (outstanding[5]?.[0] !== "Reference" || outstanding[5]?.[1] !== "Checklist item ID")
      throw Error("The outstanding-information columns do not match the template.");
    const requests = new Set<string>();
    for (const cells of outstanding.slice(6)) {
      if (!cells.slice(1).some((v) => text(v))) continue;
      const reference = text(cells[0]),
        id = text(cells[1]);
      if (!buildingHandoverCatalogue.some((item) => item.id === id))
        throw Error(
          `Link outstanding request ${reference || "(no reference)"} to a valid checklist item ID before importing.`,
        );
      if (!reference || requests.has(reference))
        throw Error("Each populated outstanding request needs a unique reference.");
      requests.add(reference);
      if (!text(cells[2])) throw Error(`Describe the information needed for ${reference}.`);
      const row = result[id] ?? blankBuildingHandoverRow(),
        due = date(cells[8]);
      const labels = [
        "Reference",
        "Checklist item ID",
        "Information / issue",
        "Operational impact",
        "Priority",
        "Requested from",
        "Incoming owner",
        "Date requested",
        "Agreed due date",
        "Status reported by agent",
        "Next action / latest response",
        "Received date",
        "Reported closure outcome / evidence",
        "Reported reviewer / date",
      ];
      const detail = labels
        .map((label, index) => {
          const value = [7, 8, 11].includes(index) ? date(cells[index]) : text(cells[index]);
          return value ? `${label}: ${value}` : "";
        })
        .filter(Boolean)
        .join("\n");
      result[id] = {
        ...row,
        holder: row.holder || text(cells[5]),
        dueDate: row.dueDate && due ? (row.dueDate < due ? row.dueDate : due) : row.dueDate || due,
        nextAction: [
          row.nextAction,
          `${reference}: ${text(cells[2])}${text(cells[10]) ? " — " + text(cells[10]) : ""}`,
        ]
          .filter(Boolean)
          .join("\n"),
        notes: [
          row.notes,
          "Returned outstanding-information record (requires our review):\n" + detail,
        ]
          .filter(Boolean)
          .join("\n\n"),
        review: "Not reviewed",
      };
      if (
        result[id]!.notes.length > 5000 ||
        result[id]!.nextAction.length > 5000 ||
        result[id]!.holder.length > 240
      )
        throw Error(
          `Outstanding requests for ${id} exceed the entry limit. Split or shorten the notes before importing.`,
        );
    }
  }
  if (Object.keys(result).length === 0) throw Error("No completed checklist entries were found.");
  return result;
}
